import React from 'react';
import { Box, Container, Grid, Typography, Card, CardContent, Button, useTheme, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link, useNavigate } from 'react-router-dom';

const ProductsSolutions = ({ onSolutionsClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const handleSolutionsClick = (event) => {
    if (onSolutionsClick) {
      onSolutionsClick(event);
    } else {
      // If no handler provided, navigate to solutions page
      navigate('/solutions');
    }
  };

  return (
    <Box id="solutions" sx={{ py: { xs: 6, sm: 8, md: 12 }, backgroundColor: '#fff' }}>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              color: 'primary.main',
              mb: { xs: 2, md: 2 },
              fontWeight: 600,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            }}
          >
            Ready to transform your business?
          </Typography>
          <Typography
            variant="h5"
            align="center"
            sx={{
              color: '#666',
              mb: { xs: 4, sm: 6, md: 8 },
              maxWidth: '800px',
              mx: 'auto',
              fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
              px: { xs: 2, md: 0 },
            }}
          >
            Discover how our geospatial solutions can revolutionize your operations
          </Typography>
        </motion.div>

        <Grid 
          container 
          spacing={{ xs: 3, sm: 4, md: 6 }}
          justifyContent="center"
          alignItems="center"
          sx={{ 
            maxWidth: '1200px',
            mx: 'auto',
            px: { xs: 1, md: 0 },
          }}
        >
          {/* Products Card */}
          <Grid item xs={12} md={5.5} sx={{ display: 'flex', justifyContent: 'center' }}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              style={{ width: '100%', maxWidth: '500px' }}
            >
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  overflow: 'hidden',
                  transition: 'transform 0.3s ease',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
                  },
                }}
              >
                <Box
                  sx={{
                    height: { xs: 200, sm: 240, md: 280 },
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  <Box
                    component="img"
                    src="/images/pro.jpg"
                    alt="GIS Products"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      filter: 'brightness(0.85)',
                    }}
                  />
                </Box>
                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                  <Typography 
                    variant="h4" 
                    gutterBottom 
                    sx={{ 
                      color: 'primary.main', 
                      fontWeight: 600,
                      fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                    }}
                  >
                    Products
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mb: 3, 
                      color: '#666', 
                      minHeight: '80px',
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      lineHeight: 1.6,
                    }}
                  >
                    Our comprehensive portfolio of geospatial products can help you automate the management of your spatial data. From desktop applications to web-based solutions.
                  </Typography>
                  <Button
                    component={Link}
                    to="/products"
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    fullWidth={isMobile}
                    sx={{
                      bgcolor: '#2196f3',
                      px: { xs: 3, md: 4 },
                      py: 1.5,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      '&:hover': {
                        bgcolor: '#1976d2',
                      },
                    }}
                  >
                    PRODUCTS
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Solutions Card */}
          <Grid item xs={12} md={5.5} sx={{ display: 'flex', justifyContent: 'center' }}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              style={{ width: '100%', maxWidth: '500px' }}
            >
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  overflow: 'hidden',
                  transition: 'transform 0.3s ease',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
                  },
                }}
              >
                <Box
                  sx={{
                    height: { xs: 200, sm: 240, md: 280 },
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  <Box
                    component="img"
                    src="/images/sol.jpg"
                    alt="GIS Solutions"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      filter: 'brightness(0.85)',
                    }}
                  />
                </Box>
                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                  <Typography 
                    variant="h4" 
                    gutterBottom 
                    sx={{ 
                      color: 'primary.main', 
                      fontWeight: 600,
                      fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                    }}
                  >
                    Solutions
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mb: 3, 
                      color: '#666', 
                      minHeight: '80px',
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      lineHeight: 1.6,
                    }}
                  >
                    We recognize that location data is ubiquitous and the potential innovations with it are limitless. Our custom solutions help you harness this power.
                  </Typography>
                  <Button
                    onClick={handleSolutionsClick}
                    to="/solutions"
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    fullWidth={isMobile}
                    sx={{
                      bgcolor: '#4caf50',
                      px: { xs: 3, md: 4 },
                      py: 1.5,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      '&:hover': {
                        bgcolor: '#388e3c',
                      },
                    }}
                  >
                    SOLUTIONS
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ProductsSolutions; 